import { supabase } from '../client';
import { Tables } from '../types';
import { toManualUserId } from '@/types';

type ScoreType = Tables<'scores'>['score_type'];

/**
 * Fetches scores with optional filtering parameters
 */
export const getScores = async (params?: {
  userId?: string;
  userIds?: string[];
  manualPlayerId?: string;
  manualPlayerIds?: string[];
  courseId?: string;
  gameId?: string;
  holeNumber?: number;
  scoreType?: ScoreType;
}) => {
  let query = supabase.from('scores').select('*');

  if (params?.manualPlayerId) {
    query = query.eq('manual_player_id', params.manualPlayerId);
  } else if (params?.manualPlayerIds && params.manualPlayerIds.length > 0) {
    query = query.in('manual_player_id', params.manualPlayerIds);
  } else if (params?.userId) {
    // Exclude scores attributed to manual players (user_id is just FK owner)
    query = query.eq('user_id', params.userId).is('manual_player_id', null);
  } else if (params?.userIds && params.userIds.length > 0) {
    query = query.in('user_id', params.userIds).is('manual_player_id', null);
  }
  
  if (params?.courseId) {
    query = query.eq('course_id', params.courseId);
  }
  
  if (params?.holeNumber) {
    query = query.eq('hole_number', params.holeNumber);
  }
  
  if (params?.scoreType) {
    query = query.eq('score_type', params.scoreType);
  }
  
  // For gameId, we need to join with score_games
  if (params?.gameId) {
    const { data: scoreIds, error: linkError } = await supabase
      .from('score_games')
      .select('score_id')
      .eq('game_id', params.gameId);
    
    if (linkError) {
      console.error('Error fetching score game links:', linkError);
      return [];
    }
    
    if (scoreIds && scoreIds.length > 0) {
      const ids = scoreIds.map(row => row.score_id);
      query = query.in('id', ids);
    } else {
      // No scores for this game
      return [];
    }
  }
  
  const { data, error } = await query;
  
  if (error) {
    console.error('Error fetching scores:', error);
    return [];
  }
  
  return data || [];
};

/**
 * Get game scores for a specific hole
 */
export const getGameScores = async (gameId: string, holeNumber?: number) => {
  try {
    // First get the game to find the course ID
    const { data: game, error: gameError } = await supabase
      .from('games')
      .select('course_id')
      .eq('id', gameId)
      .single();
    
    if (gameError) {
      console.error('Error fetching game:', gameError);
      return [];
    }
    
    // Get all scores that are linked to this game via score_games
    const { data: scoreGames, error: linkError } = await supabase
      .from('score_games')
      .select('score_id')
      .eq('game_id', gameId);
    
    if (linkError) {
      console.error('Error fetching score game links:', linkError);
      return [];
    }
    
    if (!scoreGames || scoreGames.length === 0) {
      return [];
    }
    
    // Get the scores
    let query = supabase
      .from('scores')
      .select(`
        *,
        profiles:user_id(*),
        manual_players:manual_player_id(id, name)
      `)
      .in('id', scoreGames.map(sg => sg.score_id))
      .eq('course_id', game.course_id);
      
    if (holeNumber) {
      query = query.eq('hole_number', holeNumber);
    }
    
    const { data: scores, error: scoresError } = await query;
    
    if (scoresError) {
      console.error('Error fetching scores:', scoresError);
      return [];
    }
    
    return scores || [];
  } catch (error) {
    console.error('Unexpected error in getGameScores:', error);
    return [];
  }
};

/**
 * Get all players present for a score (real + manual).
 * Returns objects with { id, name, email, image_url } shape.
 * Manual players use prefixed IDs and have isManual: true.
 */
export const getScorePresencePlayers = async (scoreId: string) => {
  const { data, error } = await supabase
    .from('score_presence')
    .select('profile_id, manual_player_id, profiles:profile_id(id, name, email, image_url), manual_players:manual_player_id(id, name, email)')
    .eq('score_id', scoreId);

  if (error) {
    console.error('Error fetching score presence:', error);
    return [];
  }

  if (!data) return [];

  return data.map(item => {
    const mp = item.manual_players as { id: string; name: string; email: string | null } | null;
    if (mp) {
      return {
        id: toManualUserId(mp.id),
        name: mp.name,
        email: mp.email,
        image_url: null,
        isManual: true,
        manualPlayerId: mp.id,
      };
    }
    return item.profiles;
  }).filter(Boolean);
};

/**
 * Get game scores for a specific hole based on player presence
 */
export const getGameScoresByPresence = async (gameId: string, holeNumber?: number) => {
  try {
    // First get the game to find the course ID
    const { data: game, error: gameError } = await supabase
      .from('games')
      .select('course_id')
      .eq('id', gameId)
      .single();
    
    if (gameError) {
      console.error('Error fetching game:', gameError);
      return [];
    }
    
    // Get all players in the game
    const { data: gamePlayers, error: playersError } = await supabase
      .from('game_players')
      .select('player_id')
      .eq('game_id', gameId);
    
    if (playersError) {
      console.error('Error fetching game players:', playersError);
      return [];
    }
    
    if (!gamePlayers || gamePlayers.length === 0) {
      return [];
    }
    
    const playerIds = gamePlayers.map(gp => gp.player_id);
    
    // Find scores where any of the game's players were present
    const { data: scorePresence, error: presenceError } = await supabase
      .from('score_presence')
      .select('score_id')
      .in('profile_id', playerIds);
    
    if (presenceError) {
      console.error('Error finding scores by presence:', presenceError);
      return [];
    }
    
    if (!scorePresence || scorePresence.length === 0) {
      return [];
    }
    
    // Get unique score IDs
    const scoreIds = [...new Set(scorePresence.map(sp => sp.score_id))];
    
    // Get the actual scores
    let scoresQuery = supabase
      .from('scores')
      .select(`
        *, 
        profiles:user_id(*)
      `)
      .in('id', scoreIds)
      .eq('course_id', game.course_id);
    
    if (holeNumber) {
      scoresQuery = scoresQuery.eq('hole_number', holeNumber);
    }
    
    const { data: scores, error: scoresError } = await scoresQuery;
    
    if (scoresError) {
      console.error('Error fetching scores:', scoresError);
      return [];
    }
    
    return scores || [];
  } catch (error) {
    console.error('Unexpected error in getGameScoresByPresence:', error);
    return [];
  }
};
